import { useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText as GSAPSplitText } from 'gsap/SplitText'
import { Typography } from '@mui/material'
gsap.registerPlugin(ScrollTrigger, GSAPSplitText)

const SplitText = ({
  text,
  className = '',
  delay = 100,
  duration = 0.6,
  ease = 'power3.out',
  splitType = 'chars',
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  rootMargin = '-100px',
  textAlign = 'center',
  variant = 'body1',
  sx = {},
  onLetterAnimationComplete,
}) => {
  const ref = useRef(null)
  const animationCompletedRef = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el || animationCompletedRef.current) return

    let splitter

    // ✅ Esperar a que las fuentes estén listas ANTES de ejecutar GSAP SplitText
    document.fonts.ready.then(() => {
      const absoluteLines = splitType === 'lines'
      if (absoluteLines) el.style.position = 'relative'

      splitter = new GSAPSplitText(el, {
        type: splitType,
        absolute: absoluteLines,
        linesClass: 'split-line',
      })

      let targets
      switch (splitType) {
        case 'lines':
          targets = splitter.lines
          break
        case 'words':
          targets = splitter.words
          break
        case 'words, chars':
          targets = [...splitter.words, ...splitter.chars]
          break
        default:
          targets = splitter.chars
      }

      targets.forEach(t => {
        t.style.willChange = 'transform, opacity'
      })

      const startPct = (1 - threshold) * 100
      const m = /^(-?\d+)px$/.exec(rootMargin)
      const raw = m ? parseInt(m[1], 10) : 0
      const sign = raw < 0 ? `-=${Math.abs(raw)}px` : `+=${raw}px`
      const start = `top ${startPct}%${sign}`

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: 'play none none none',
          once: true,
        },
        smoothChildTiming: true,
        onComplete: () => {
          animationCompletedRef.current = true
          gsap.set(targets, {
            ...to,
            clearProps: 'willChange',
            immediateRender: true,
          })
          onLetterAnimationComplete?.()
        },
      })

      tl.set(targets, { ...from, immediateRender: false, force3D: true })
      tl.to(targets, {
        ...to,
        duration,
        ease,
        stagger: delay / 1000,
        force3D: true,
      })
    })

    return () => {
      if (splitter) splitter.revert()
      ScrollTrigger.getAll().forEach(t => t.kill())
    }
  }, [
    text,
    delay,
    duration,
    ease,
    splitType,
    from,
    to,
    threshold,
    rootMargin,
    onLetterAnimationComplete,
  ])

  return (
    <Typography
      ref={ref}
      className={`split-parent ${className}`}
      variant={variant}
      align={textAlign}
      sx={{
        overflow: 'hidden',
        display: 'inline-block',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        ...sx,
      }}>
      {text}
    </Typography>
  )
}

export default SplitText
